import { useNavigate, useRouterState } from '@tanstack/react-router';

function serializeValue(value: string | boolean): string | undefined {
  if (typeof value === 'boolean') {
    return value ? 'true' : undefined;
  }

  return value || undefined;
}

/**
 * Reads and writes a single toolbar value from a URL query parameter.
 *
 * The URL is the only source of truth — the toolbar components keep no
 * internal state, so their value survives a reload and can be bookmarked or
 * shared. Writes replace the current history entry, otherwise every keystroke
 * in a search field would add one.
 *
 * Booleans are stored as the string `'true'`; a `false` or empty value removes
 * the parameter from the URL.
 *
 * @example
 * const [value, setValue] = useToolbarState('', 'q');
 * // → reads ?q=... from URL; setValue('foo') → navigates to ?q=foo
 */
export function useToolbarState<T extends string | boolean>(
  defaultValue: T,
  queryParamKey: string,
): [T, (value: T) => void] {
  const rawParam = useRouterState({
    select: (state) =>
      new URLSearchParams(state.location.searchStr).get(queryParamKey),
  });

  const navigate = useNavigate();

  const value = (
    typeof defaultValue === 'boolean'
      ? rawParam === 'true'
      : (rawParam ?? defaultValue)
  ) as T;

  const setValue = (next: T) => {
    const serialized = serializeValue(next);

    // global useNavigate search-function typing doesn't align with the app's
    // typed route search params; use routeApi.useNavigate() in route-specific
    // code for full type safety.
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    (navigate as any)({
      search: (prev: Record<string, string | undefined>) => ({
        ...prev,
        [queryParamKey]: serialized,
      }),
      replace: true,
    });
  };

  return [value, setValue];
}
